import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    product: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.phone) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          product: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 sm:gap-10 md:gap-12 items-center">
          {/* Left Visual Area */}
          <div className="md:col-span-2">
            <div className="rounded-2xl sm:rounded-3xl overflow-hidden mb-6 sm:mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/25879a05089aab46210fc3f934b466e2.jpg"
                alt="Export Operations"
                className="w-full h-64 sm:h-80 md:h-full object-cover object-top"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white leading-tight">
                Start Your
                <br />
                Export Journey
                <br />
                With Us Today
              </h2>
            </div>
          </div>

          {/* Right Form Area */}
          <div className="md:col-span-3">
            <p className="text-gray-400 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
              Ready to expand your business globally? Get in touch with our export specialists and discover how we can help you succeed
            </p>

            <form
              id="contact-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d5np2rs8q3pa76svmkig"
              method="POST"
              onSubmit={handleSubmit}
              className="space-y-4 sm:space-y-5"
            >
              {/* Full Name */}
              <div>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Full Name *"
                  required
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                />
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address *"
                  required
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                />
              </div>

              {/* Phone Number */}
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number *"
                  required
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                />
              </div>

              {/* Product Interest */}
              <div>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleChange}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-2 border-gray-700 rounded-xl text-white focus:border-emerald-500 focus:outline-none transition-colors text-sm cursor-pointer"
                >
                  <option value="" className="bg-gray-900">
                    Select Product Interest
                  </option>
                  <option value="Pulses" className="bg-gray-900">
                    Pulses
                  </option>
                  <option value="Spices" className="bg-gray-900">
                    Spices
                  </option>
                  <option value="Rice" className="bg-gray-900">
                    Rice
                  </option>
                  <option value="Maize" className="bg-gray-900">
                    Maize
                  </option>
                  <option value="Coffee" className="bg-gray-900">
                    Coffee Powder
                  </option>
                  <option value="Tea" className="bg-gray-900">
                    Tea Powder
                  </option>
                  <option value="Multiple Products" className="bg-gray-900">
                    Multiple Products
                  </option>
                </select>
              </div>

              {/* Message */}
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message (Optional)"
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-transparent border-2 border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none transition-colors resize-none text-sm"
                ></textarea>
                <p className="text-gray-500 text-xs mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-blue-600 text-white rounded-full text-sm sm:text-base font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-center"
                >
                  {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
                {submitStatus === 'success' && (
                  <p className="text-emerald-400 text-sm text-center sm:text-left">
                    Message sent successfully!
                  </p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-400 text-sm text-center sm:text-left">
                    Please fill in all required fields
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
